import * as THREE from 'three'
import Experience from '../Experience.js'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'

export default class Camera {
    constructor(settings = {}) {
        this.experience = new Experience()
        this.resizer = this.experience.resizer
        this.scene = this.experience.scene
        this.canvas = this.experience.canvas
        this.cameraType = settings.camera_type || 'perspective'

        this.setInstance(settings)
        this.setControls(settings)
    }

    get position() { return this.instance.position }

    setInstance(settings) {
        const width = this.resizer.currentWidth;
        const minHeight = this.resizer.minimumHeight;
        const height = Math.max(this.resizer.currentHeight, minHeight);
        this.startWidth = width;
        this.startHeight = height;
        this.startAspect = width / height;
        this.startFov = settings.camera_fov || 55;

        const pos = {
            x: settings.camera_x ?? 0,
            // y: settings.camera_y ?? -3,
            y: settings.camera_y ?? 0,
            z: settings.camera_z ?? 7
        };

        if (this.cameraType === 'orthographic') {
            this.instance = this.createOrthographic(pos.z, width, height);
        } else {
            this.instance = new THREE.PerspectiveCamera(this.startFov, this.startAspect, 0.1, 100);
        }

        this.instance.position.set(pos.x, pos.y, pos.z);
        this.scene.add(this.instance);
    }

    createOrthographic(z, width, height) {
        const frustum = z * 0.5;
        const aspect = width / height;
        return new THREE.OrthographicCamera(
            -frustum * aspect, frustum * aspect,
            frustum, -frustum,
            0.1, 100
        );
    }

    setControls(settings) {
        this.controls = new OrbitControls(this.instance, this.canvas);
        this.controls.enableDamping = true;
        this.controls.rotateSpeed = 0.54;
        this.controls.enabled = !!settings.orbit_controls_enabled;
    }

    applySettings(settings) {
        const hasValue = (value) => value !== undefined && value !== null;
        let needsVignetteRescale = false;

        // Switch camera type
        if (hasValue(settings.camera_type) && settings.camera_type !== this.cameraType) {
            this.cameraType = settings.camera_type;
            const pos = { ...this.instance.position };

            // Detach vignette mesh before removing old camera
            const vignetteMesh = this.experience.vignetteMesh;
            if (vignetteMesh) this.instance.remove(vignetteMesh);

            this.scene.remove(this.instance);
            if (this.controls) this.controls.dispose();

            const width = this.resizer.currentWidth;
            const height = Math.max(this.resizer.currentHeight, this.resizer.minimumHeight);
            const aspect = width / height;
            const fov = hasValue(settings.camera_fov) ? settings.camera_fov : this.startFov;

            if (this.cameraType === 'orthographic') {
                this.instance = this.createOrthographic(pos.z, width, height);
            } else {
                this.instance = new THREE.PerspectiveCamera(fov, aspect, 0.1, 100);
            }

            this.instance.position.set(pos.x, pos.y, pos.z);
            this.scene.add(this.instance);
            this.setControls(settings);

            // Re-parent vignette mesh to new camera
            if (vignetteMesh) this.instance.add(vignetteMesh);

            // Update renderer to use new camera
            if (this.experience.renderer) {
                this.experience.renderer.updateCamera(this.instance);
            }

            needsVignetteRescale = true;
        }

        if (hasValue(settings.camera_x)) this.instance.position.x = settings.camera_x;
        if (hasValue(settings.camera_y)) this.instance.position.y = settings.camera_y;

        if (hasValue(settings.camera_z)) {
            this.instance.position.z = settings.camera_z;
            if (this.instance.isOrthographicCamera) {
                const width = this.resizer.currentWidth;
                const height = Math.max(this.resizer.currentHeight, this.resizer.minimumHeight);
                const frustum = this.instance.position.z * 0.5;
                const aspect = width / height;
                this.instance.left = -frustum * aspect;
                this.instance.right = frustum * aspect;
                this.instance.top = frustum;
                this.instance.bottom = -frustum;
                this.instance.updateProjectionMatrix();
                needsVignetteRescale = true;
            }
        }

        if (hasValue(settings.camera_fov) && this.instance.fov !== undefined) {
            this.instance.fov = settings.camera_fov;
            this.instance.updateProjectionMatrix();
            needsVignetteRescale = true;
        }

        if (hasValue(settings.orbit_controls_enabled) && this.controls) {
            this.controls.enabled = !!settings.orbit_controls_enabled;
        }

        if (needsVignetteRescale && this.experience.vignetteModifiers?.rescale) {
            this.experience.vignetteModifiers.rescale();
        }
    }

    resize() {
        const minHeight = this.resizer.minimumHeight;
        const height = Math.max(this.resizer.currentHeight, minHeight);
        const width = this.resizer.currentWidth;

        if (this.instance.isOrthographicCamera) {
            const frustum = this.instance.position.z * 0.5;
            const aspect = width / height;
            this.instance.left = -frustum * aspect;
            this.instance.right = frustum * aspect;
            this.instance.top = frustum;
            this.instance.bottom = -frustum;
        } else {
            this.instance.aspect = width / height;
        }
        this.instance.updateProjectionMatrix();
    }

    update() {
        if (this.controls?.enabled) {
            this.controls.update();
        }
    }
}
